import { DataQualityService, SchemaService, ServerService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DeserializedServer, Operation, SchemaProperty, ServerBulkDeleteParams, ServerBulkUpdateParams, ServerDownloadParams,
    ServerFilterOption, ServerSortOption, ServerSortOrder, UOMS, UomTypes } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, Box, Button, Modal, RadioGroup, RadioGroupProps, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { match, useLocation, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { DataQualityEnabledTypes } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { localization } from '../../common/table/constants';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes, Query } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { uomConversion } from '../../common/uomConversion';
import { deepCopy, format, generatePluralStr } from '../../common/util';
import DataQualityRuleValidation from '../data-quality/DataQualityRuleValidation';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { ColumnOrderType, EXCLUDED_SERVER_COLUMNS, FILTER_TOKEN_DISPLAY_LIMIT, OVERSIZED_RESOURCE_THRESHOLDS, SERVER_ID_COLUMN } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { FilterLocationState, SchemaPropertyMap } from './types';
import { generateColumnDefs, getFilterQuery } from './utils';

type ServerProps = FlashContextInterface;

const serverService = new ServerService();
const schemaService = new SchemaService();
const dataQualityService = new DataQualityService();
const resourceName = 'servers';
const dataQualityResourceName = 'server';

enum DownloadOptions {
    DOWNLOAD_ALL = 'downloadAll',
    DOWNLOAD_FILTERED = 'downloadFiltered'
}

const downloadOptions: RadioGroupProps.RadioButtonDefinition[] = [
    {
        value: DownloadOptions.DOWNLOAD_ALL,
        label: localization.DOWNLOAD_ALL,
        description: localization.DOWNLOAD_ALL_DESCRIPTION
    },
    {
        value: DownloadOptions.DOWNLOAD_FILTERED,
        label: localization.DOWNLOAD_FILTERED,
        description: localization.DOWNLOAD_FILTERED_DESCRIPTION
    }
];

const Server: FunctionComponent<ServerProps> = ({flash}) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const location = useLocation<FilterLocationState>();
    const portfolioId = match?.params?.portfolioId;
    const [ serverItems, setServerItems ] = useState<DeserializedServer[]>([]);
    const [ serverSchemas, setServerSchemas ] = useState<SchemaProperty[]>([]);
    const [ serverSchemaMap, setServerSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ serverColumnDefinitions, setServerColumnDefinitions ] = useState<ColumnDefinition<DeserializedServer>[]>([]);
    const [ selectedServers, setSelectedServers ] = useState<DeserializedServer[]>([]);
    const [ filteredServers, setFilteredServers ] = useState<DeserializedServer[] | null>(null);
    const [ sortOptions, setSortOptions ] = useState<ServerSortOption[]>([]);
    const [ filterOptions, setFilterOptions ] = useState<ServerFilterOption | null>({ filters: [], operation: Operation.AND });
    const [ loading, setLoading ] = useState(true);
    const [ reloadRulesImmediately, setReloadRulesImmediately ] = useState(true);
    const [ defaultQuery, setDefaultQuery ] = useState<Query>();
    const [ downloadModalVisible, setDownloadModalVisible ] = useState(false);
    const [ downloading, setDownloading ] = useState(false);
    const [ selectedDownloadOption, setSelectedDownloadOption ] = useState(downloadOptions[0].value);
    const [ oversizedAlert, setOversizedAlert ] = useState('');
    const { updateInlineEditFn } = useContext(InlineEditContext);
    const { alert } = flash;
   
    useEffect(() => {
        if (location?.state?.targetValue && location?.state?.targetColumn) {
            const query = getFilterQuery(location.state.targetValue, location.state.targetColumn);
            setDefaultQuery(query);
        }
    }, [ location ]);

    const isMounted = useIsMounted();

    const loadServerData = useCallback(async () => {
        setLoading(true);
        setSelectedServers([]);
        try {
            if (!portfolioId) {
                alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                return;
            }
            // Load server data
            const servers = await serverService.list(portfolioId);
            if (!isMounted()) {
                return;
            }
            if (servers.length > OVERSIZED_RESOURCE_THRESHOLDS.server) {
                setOversizedAlert(format(constants.OVERSIZED_PORTFOLIO, [servers.length, resourceName, OVERSIZED_RESOURCE_THRESHOLDS.server]));
            } else {
                setOversizedAlert('');
            }
            setServerItems(servers);
           
            // Load server schemas
            const serverSchemas = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            setServerSchemas(serverSchemas);
            const serverHeaders: SchemaPropertyMap = {};
            serverSchemas.forEach(serverSchema => {
                serverHeaders[serverSchema.value] = serverSchema;
            });
            setServerSchemaMap(serverHeaders);

            // Build column definitions
            const columnDefinitions = generateColumnDefs(servers, serverHeaders, ColumnOrderType.SERVER, EXCLUDED_SERVER_COLUMNS, true);
            // Add mapped columns
            columnDefinitions.push({
                id: 'applicationMapped',
                header: constants.APPLICATION_MAPPED_COLUMN_HEADER,
                type: ColumnTypes.TEXT,
                data: (item: DeserializedServer) => item.applicationMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                cell: (item: DeserializedServer) => item.applicationMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
            });
            columnDefinitions.push({
                id: 'databaseMapped',
                header: constants.DATABASE_MAPPED_COLUMN_HEADER,
                type: ColumnTypes.TEXT,
                data: (item: DeserializedServer) => item.databaseMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                cell: (item: DeserializedServer) => item.databaseMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
            });
            setServerColumnDefinitions(columnDefinitions);
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, (err as Error).message, constants.SERVER_ERROR_LOADING);
                setServerItems([]);
                setServerColumnDefinitions([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }, [ alert, isMounted, portfolioId ]);

    useEffect(() => {
        loadServerData();
    }, [ loadServerData ]);

    const inlineEditFunction = useCallback(async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: ServerBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            serverIds: [(rowItem as DeserializedServer).serverId],
        };

        try {
            await serverService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.SERVER_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.SERVER_EDITING_ERROR_HEADING);
        } finally {
            await loadServerData();
        }
    }, [ alert, loadServerData, portfolioId ]);

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    }, [ inlineEditFunction, updateInlineEditFn ]);

    /**
     * Delete multiple servers. If servers are selected, they will be deleted. If there are no selected servers,
     * then all filtered servers will be deleted.
     * @returns void
     */
    const onConfirmBulkDeletion = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        setReloadRulesImmediately(false);
        try {
            const serverIds: string[] = (selectedServers.length || !filteredServers ? selectedServers : filteredServers)
                .map(selectedServer => selectedServer.serverId);
            const conditions: ServerBulkDeleteParams = {
                serverIds,
                portfolioId,
            };
            await serverService.deleteServers(conditions);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_DELETE_ERROR_HEADING);
        } finally {
            await loadServerData();
        }
    };

    const onConfirmDeleteAll = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        setReloadRulesImmediately(true);
        try {
            await serverService.clear(portfolioId); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_DELETE_ERROR_HEADING);
        } finally {
            await loadServerData();
        }
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: InlineEditValue,
        updatedUOM?: UOMS,
    ) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        setReloadRulesImmediately(false);
        const selectedSchema = serverSchemaMap[selectedHeader];
        if (!selectedHeader || !selectedSchema) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.SERVER_EDITING_ERROR_HEADING);
            return;
        }
        const selectedServerIds = (selectAll ? (filteredServers ?? serverItems) : selectedServers).map(selectedServer => selectedServer.serverId);

        let bulkUpdateValue = updatedValue;
        
        // Allow user to unset percentage properties
        if (selectedSchema?.UOM == UomTypes.PERCENTAGE && 
            (!['number', 'string'].includes(typeof(updatedValue)) || updatedValue === '')) {
            bulkUpdateValue = null;
        } else if (selectedSchema.UOM && selectedSchema.dbUOM && updatedUOM) {
            const dbUOM = selectedSchema.dbUOM;
            bulkUpdateValue = uomConversion(Number(updatedValue), updatedUOM, dbUOM);
        }

        const conditions: ServerBulkUpdateParams = {
            fieldName: selectedHeader,
            fieldValue: bulkUpdateValue,
            portfolioId,
            serverIds: selectedServerIds,
        };

        try {
            await serverService.update(conditions);
            const updateSuccessMsg = generatePluralStr(selectedServerIds.length || serverItems.length, constants.SERVER_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_EDITING_ERROR_HEADING);
        } finally {
            await loadServerData();
        }
    };

    const defaultSortingState: SortingState<DeserializedServer> = {
        isDescending: false,
        sortingColumn: {
            sortingField: SERVER_ID_COLUMN,
        },
    };

    const onFilterByRuleChange = async (ruleId: string) => {
        if (!portfolioId) {
            return;
        }
        try {
            const invalidServerIds = await dataQualityService.getInvalidRecords(portfolioId, dataQualityResourceName, ruleId);
            const query = getFilterQuery(invalidServerIds, SERVER_ID_COLUMN);
            setDefaultQuery(query);
        } catch (err) {
            flash.alert(FlashType.error, constants.RULE_FILTER_ERROR_MSG, '');
        }
    };

    const filterChanged = (servers: readonly DeserializedServer[] | null, query: Query | undefined) => {
        setFilterOptions({
            operation: query?.operation as Operation ?? Operation.AND,
            filters: query?.tokens.map(token => {
                return {
                    column: token.propertyKey ?? '', 
                    operator: mapOperator(token.operator),
                    value: token.value
                };
            }) ?? []
        });
        
        setFilteredServers(deepCopy(servers));
    };

    const mapOperator = (input: string): string => {
        switch (input){
            case ':':
                return 'contains';
            case '!:':
                return 'notContains';
            default:
                return input;
        }
    };

    const sortingChanged = (isDescending?: boolean, sortingField?: string) => {
        setSortOptions([{ order: isDescending ? ServerSortOrder.ASC : ServerSortOrder.DESC, column: sortingField ?? '' }]);
    };

    const serverDownloadClick = async () => {
        setDownloading(true);
        try {
            const params: ServerDownloadParams = selectedDownloadOption === DownloadOptions.DOWNLOAD_ALL ?
                {
                    sortOptions: [],
                    filterOptions: {
                        filters: [],
                        operation: Operation.AND
                    }
                } :
                {
                    sortOptions: sortOptions,
                    filterOptions: filterOptions ?? {
                        filters: [],
                        operation: Operation.AND
                    }
                };

            const fileUrl: string = await serverService.download(portfolioId ?? '', params);
            const win = window.open(fileUrl);
            if (!win){
                flash.alert(FlashType.warning, constants.ERROR_OPENING_FILE_URL_CONTENT, constants.ERROR_OPENING_FILE_URL_HEADER);
            }
            setDownloadModalVisible(false);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.SERVER_ERROR_DOWNLOAD);
        } finally {
            setDownloading(false);
        }
    };

    const serverDownloadModal = (
        <Modal
            onDismiss={() => setDownloadModalVisible(false)}
            visible={downloadModalVisible}
            size='medium'
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Button variant='link' onClick={() => setDownloadModalVisible(false)}>{localization.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant='primary' loading={downloading} onClick={serverDownloadClick}>{localization.DOWNLOAD_BUTTON_TEXT}</Button>
                    </SpaceBetween> 
                </Box>
            }
            header={localization.DOWNLOAD_XSLX}
        >
            <RadioGroup 
                value={selectedDownloadOption}
                items={downloadOptions}
                onChange={({ detail }) => setSelectedDownloadOption(detail.value)}
            />
        </Modal>
    );

    return (
        <SpaceBetween size='m'>
            {oversizedAlert ? 
                <Alert type='info' header={constants.OVERSIZED_HEADER}>{oversizedAlert}</Alert>
                : (
                    <DataQualityRuleValidation 
                        assetType={DataQualityEnabledTypes.SERVER}
                        assetItems={serverItems}
                        reloadImmediately={reloadRulesImmediately}
                        filterOnRule={onFilterByRuleChange}
                    />
                )
            }
            
            <AWSCATTable
                resourceName={constants.SERVER_TABLE_HEADER}
                resourceType='server'
                items={serverItems}
                importWhenEmpty={true}
                columnDefinitions={serverColumnDefinitions}
                loading={loading}
                resizeable={true}
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedServers as () => void}
                onFilterChange={filterChanged}
                onSortingChange={sortingChanged}
                downloadFn={() => setDownloadModalVisible(true)}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='server'
                        resourceName={constants.SERVER_RESOURCE_NAME}
                        resourceCount={serverItems.length}
                        selectionCount={selectedServers.length}
                        hasFilter={!!filteredServers}
                        filteredCount={filteredServers?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={serverSchemas}
                        schemaPropertyMap={serverSchemaMap}
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
                defaultQuery={defaultQuery}
                filterTokenDisplayLimit={FILTER_TOKEN_DISPLAY_LIMIT}
            />
            {downloadModalVisible && serverDownloadModal}
        </SpaceBetween>
    );
};

export default withFlashContext(Server);
